import { useEffect, useState } from "react";
import { Box, Paper, Typography, TextField, Checkbox, FormControlLabel, Button, IconButton, Accordion, AccordionSummary, AccordionDetails, Grid } from "@mui/material";
import { colors } from "../../theme/muiColors";
import { ExpandMore, Add, Delete, Save } from "@mui/icons-material";
import { decodeToken } from "../../utils";
import { useServiceConfiguration } from "./hooks/useServiceConfiguration";

const formatKeyLabel = (key) =>
  key.replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const token = sessionStorage.getItem("token");
const decode = decodeToken(token);
const companyId = decode?.companies?.[0]?.UsersCompany?.companyId;


const DEFAULT_RANGE = {
  minSqFt: 0,
  maxSqFt: 1000,
  basePrice: 100,
  rooms: {
    masterBedroom: { price: 15, max: 1 },
    otherRoom: { price: 25, max: 2 },
    masterBathroom: { price: 35, max: 1 },
    regularBathroom: { price: 15, max: 1 },
    halfBathroom: { price: 40, max: 1 },
  },
  areas: {
    kitchen: { price: 40, included: true },
    laundryRoom: { price: 30, included: true },
    livingRoom: { price: 25, included: false },
    stairs: { price: 18, included: true },
  },
  additionalRequests: {
    fridge: { price: 25, included: true },
    dishwasher: { price: 25, included: true },
    tidyUp: { price: 35, included: true },
    windows: { price: 18, included: true },
    oven: { price: 35, included: true },
    handDishWashing: { price: 25, included: true },
    garage: { price: 25, included: true },
  },
  hasPets: { price: 25, included: true },
};

const toUI = (p) => ({
  id: p.id,
  minSqFt: p.minSqft,
  maxSqFt: p.maxSqft,
  basePrice: p.config?.basePrice ?? 0,
  rooms: p.config?.rooms ?? {},
  areas: p.config?.areas ?? {},
  additionalRequests: p.config?.additionalRequests ?? {},
  hasPets: p.config?.hasPets ?? { price: 0, included: false },
});

export const ServiceConfig = ({ service }) => {
  const companyServiceId = service?.companyServices?.[0]?.id;
  const { configuration, putServiceConfiguration, getServiceConfiguration } =
    useServiceConfiguration();

  const [config, setConfig] = useState({ sizeRanges: [DEFAULT_RANGE] });
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!companyId || !service?.id) return;
    getServiceConfiguration(companyId, service.id);
  }, []);

  useEffect(() => {
    if (Array.isArray(configuration) && configuration.length > 0) {
      setConfig({ sizeRanges: configuration.map(toUI) });
    } else {
      setConfig({ sizeRanges: [DEFAULT_RANGE] });
    }
  }, [configuration]);

  const computeRangeErrors = (ranges) => {
    const errs = ranges.map(() => ({ min: null, max: null }));

    ranges.forEach((r, i) => {
      if (!(Number.isFinite(r.minSqFt) && Number.isFinite(r.maxSqFt))) {
        errs[i].min = "Valores inválidos.";
        errs[i].max = "Valores inválidos.";
        return;
      }
      if (r.minSqFt >= r.maxSqFt) {
        errs[i].min = "Min debe ser menor que Max.";
        errs[i].max = "Max debe ser mayor que Min.";
      }
    });

    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const a = ranges[i];
        const b = ranges[j];
        const overlapOrTouch =
          !(a.maxSqFt < b.minSqFt || a.minSqFt > b.maxSqFt);
        if (overlapOrTouch) {
          const touch =
            a.maxSqFt === b.minSqFt || a.minSqFt === b.maxSqFt;
          const msg = touch
            ? "Los rangos no pueden compartir bordes."
            : "Los rangos no deben solaparse.";
          errs[i].min = errs[i].min || msg;
          errs[i].max = errs[i].max || msg;
          errs[j].min = errs[j].min || msg;
          errs[j].max = errs[j].max || msg;
        }
      }
    }

    return errs;
  };

  useEffect(() => {
    setErrors(computeRangeErrors(config.sizeRanges));
  }, [config.sizeRanges]);

  const setRangeMin = (index, rawValue) => {
    const v = Number.isFinite(rawValue) ? Math.max(0, rawValue) : 0;
    setConfig((prev) => {
      const next = structuredClone(prev);
      const r = next.sizeRanges[index];
      if (v >= r.maxSqFt) {
        r.maxSqFt = v + 1;
      }
      r.minSqFt = v;
      return next;
    });
  };

  const setRangeMax = (index, rawValue) => {
    let v = Number.isFinite(rawValue) ? rawValue : 0;
    setConfig((prev) => {
      const next = structuredClone(prev);
      const r = next.sizeRanges[index];
      if (v <= r.minSqFt) {
        v = r.minSqFt + 1;
      }
      r.maxSqFt = v;
      return next;
    });
  };

  const handleInputChange = (path, value) => {
    setConfig((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys.at(-1)] = value;
      return next;
    });
  };

  const addSizeRange = () => {
    setConfig((prev) => {
      const last = prev.sizeRanges.at(-1) ?? DEFAULT_RANGE;
      const newMin = (last.maxSqFt ?? 0) + 1;
      const newMax = newMin + 499;
      return {
        ...prev,
        sizeRanges: [...prev.sizeRanges, { ...DEFAULT_RANGE, minSqFt: newMin, maxSqFt: newMax }],
      };
    });
  };

  const removeSizeRange = (index) => {
    setConfig((prev) =>
      prev.sizeRanges.length <= 1
        ? prev
        : { ...prev, sizeRanges: prev.sizeRanges.filter((_, i) => i !== index) }
    );
  };

  const hasErrors = errors.some((e) => e.min || e.max);

  const handleSave = async () => {
    if (hasErrors) return;
    const items = config.sizeRanges.map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      minSqft: r.minSqFt,
      maxSqft: r.maxSqFt,
      config: {
        basePrice: r.basePrice,
        rooms: r.rooms,
        areas: r.areas,
        additionalRequests: r.additionalRequests,
        hasPets: r.hasPets,
      },
    }));
    const payload = { companyId, companyServiceId, items };
    await putServiceConfiguration(payload);
  };

  return (
    <Box maxWidth="lg" mx="auto" p={4} bgcolor="var(--fx-surface-base, #FFFFFF)" minHeight="100%">
      <Paper elevation={0} sx={{ mb: 4 }}>
        <Typography sx={{ textAlign: "center" }} variant="h6" fontWeight="bold" gutterBottom>
          Service Price Configuration
        </Typography>

        <Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Button
              variant="contained"
              onClick={addSizeRange}
              startIcon={<Add />}
              size="large"
              sx={{
                px: 6,
                py: 1.5,
                minWidth: "100px",
                borderRadius: 32,
                backgroundColor: colors.blue[400],
                "&:hover": { backgroundColor: colors.blue[600] },
              }}
            >
              Add Size Range
            </Button>
          </Box>

          {config.sizeRanges.map((range, rangeIndex) => {
            const err = errors[rangeIndex] || { min: null, max: null };
            return (
              <Accordion
                sx={{ backgroundColor: "var(--fx-surface-sunken, #F8FAFC)" }}
                key={rangeIndex}
                defaultExpanded={rangeIndex === 0}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Range {rangeIndex + 1}: {range.minSqFt} - {range.maxSqFt} sq.ft
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Base Price: ${range.basePrice}
                      </Typography>
                    </Box>
                    {config.sizeRanges.length > 1 && (
                      <IconButton
                        sx={{ color: colors.red[400] }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSizeRange(rangeIndex);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={3}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle1" mb={2}>Basic Configuration</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Min Sq.Ft"
                            type="number"
                            fullWidth
                            value={range.minSqFt}
                            error={!!err.min}
                            helperText={err.min || ""}
                            onChange={(e) => {
                              const v = parseInt(e.target.value);
                              setRangeMin(rangeIndex, Number.isFinite(v) ? v : null);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Max Sq.Ft"
                            type="number"
                            fullWidth
                            value={range.maxSqFt}
                            error={!!err.max}
                            helperText={err.max || ""}
                            onChange={(e) => {
                              const v = parseInt(e.target.value);
                              setRangeMax(rangeIndex, Number.isFinite(v) ? v : null);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label="Base Price"
                            type="number"
                            fullWidth
                            value={range.basePrice}
                            onChange={(e) =>
                              handleInputChange(`sizeRanges.${rangeIndex}.basePrice`, parseInt(e.target.value) || 0)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" mb={2}>Rooms</Typography>
                      <Grid container spacing={2}>
                        {Object.entries(range.rooms).map(([roomKey, roomData]) => (
                          <Grid item xs={12} md={6} key={roomKey}>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" fontWeight="bold" gutterBottom>
                                {formatKeyLabel(roomKey)}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Price"
                                    type="number"
                                    fullWidth
                                    value={roomData.price}
                                    onChange={(e) =>
                                      handleInputChange(
                                        `sizeRanges.${rangeIndex}.rooms.${roomKey}.price`,
                                        parseInt(e.target.value)
                                      )
                                    }
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Max"
                                    type="number"
                                    fullWidth
                                    value={roomData.max}
                                    onChange={(e) =>
                                      handleInputChange(
                                        `sizeRanges.${rangeIndex}.rooms.${roomKey}.max`,
                                        parseInt(e.target.value)
                                      )
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" mb={2}>Areas</Typography>
                      <Grid container spacing={2}>
                        {Object.entries(range.areas).map(([areaKey, areaData]) => (
                          <Grid item xs={12} md={4} key={areaKey}>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" fontWeight="bold" gutterBottom>
                                {formatKeyLabel(areaKey)}
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!areaData.included}
                                    onChange={(e) =>
                                      handleInputChange(
                                        `sizeRanges.${rangeIndex}.areas.${areaKey}.included`,
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Included"
                              />
                              <TextField
                                label="Price"
                                type="number"
                                fullWidth
                                disabled={!areaData.included}
                                value={areaData.price}
                                onChange={(e) =>
                                  handleInputChange(
                                    `sizeRanges.${rangeIndex}.areas.${areaKey}.price`,
                                    parseInt(e.target.value)
                                  )
                                }
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" mb={2}>Extras</Typography>
                      <Grid container spacing={2}>
                        {Object.entries(range.additionalRequests).map(([extraKey, extraData]) => (
                          <Grid item xs={12} md={3} key={extraKey}>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="body2" fontWeight="bold" gutterBottom>
                                {formatKeyLabel(extraKey)}
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!extraData.included}
                                    onChange={(e) =>
                                      handleInputChange(
                                        `sizeRanges.${rangeIndex}.additionalRequests.${extraKey}.included`,
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Included"
                              />
                              <TextField
                                label="Price"
                                type="number"
                                fullWidth
                                value={extraData.price}
                                disabled={!extraData.included}
                                onChange={(e) =>
                                  handleInputChange(
                                    `sizeRanges.${rangeIndex}.additionalRequests.${extraKey}.price`,
                                    parseInt(e.target.value)
                                  )
                                }
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" mb={2}>Pets</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                              Has Pets
                            </Typography>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!range.hasPets?.included}
                                  onChange={(e) =>
                                    handleInputChange(
                                      `sizeRanges.${rangeIndex}.hasPets.included`,
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label="Included"
                            />
                            <TextField
                              label="Price"
                              type="number"
                              fullWidth
                              value={range.hasPets?.price}
                              disabled={!range.hasPets?.included}
                              onChange={(e) =>
                                handleInputChange(
                                  `sizeRanges.${rangeIndex}.hasPets.price`,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={<Save />}
            size="large"
            disabled={hasErrors}
            sx={{
              px: 6,
              py: 1.5,
              minWidth: "100px",
              borderRadius: 32,
              backgroundColor: hasErrors ? colors.grey[300] : colors.green[400],
              "&:hover": { backgroundColor: hasErrors ? colors.grey[300] : colors.green[600] },
            }}
          >
            Save Configuration
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
